import React, { useState, useEffect, useCallback } from 'react';
import { User, Palette, Glasses, Smile, Shuffle, Loader2, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { avatarService, AVATAR_CONFIG, FACE_DISPLAY_NAMES } from '../../../services/avatarService';
import { useAuth } from '../../../contexts/AuthContext';

const AvatarBuilder = ({ avatar, onUpdate, isEditing }) => {
  const { user } = useAuth();
  const [config, setConfig] = useState({});
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [gender, setGender] = useState('male');
  const [activeTab, setActiveTab] = useState('skinColor');

  useEffect(() => {
    const initializeAvatar = async () => {
      if (user?.id) {
        try {
          const userAvatar = await avatarService?.getUserAvatar(user?.id);
          if (userAvatar?.avatar_config && Object.keys(userAvatar?.avatar_config || {})?.length > 0) {
            setConfig(userAvatar?.avatar_config);
            if (userAvatar?.avatar_svg) {
              setPreviewUrl(`data:image/svg+xml;utf8,${encodeURIComponent(userAvatar?.avatar_svg)}`);
            } else {
              const detectedGender = userAvatar?.avatar_seed === AVATAR_CONFIG?.SEEDS?.female ? 'female' : 'male';
              setPreviewUrl(avatarService?.buildDiceBearURL(userAvatar?.avatar_config || {}, userAvatar?.avatar_seed, detectedGender));
            }
            const detectedGender = userAvatar?.avatar_seed === AVATAR_CONFIG?.SEEDS?.female ? 'female' : 'male';
            setGender(detectedGender);
          } else {
            await generatePreview({}, 'male');
          }
        } catch (err) {
          await generatePreview({}, 'male');
        }
      }
    };
    initializeAvatar();
  }, [user?.id]);

  const generatePreview = useCallback(async (newConfig, newGender) => {
    try {
      setIsLoading(true);
      setError('');
      const seed = AVATAR_CONFIG?.SEEDS?.[newGender] || AVATAR_CONFIG?.SEEDS?.male;
      const url = avatarService?.buildDiceBearURL(newConfig, seed, newGender);
      setPreviewUrl(url);
      let svgContent = null;
      try {
        const response = await fetch(url);
        if (response?.ok) svgContent = await response?.text();
      } catch {}
      if (isEditing && onUpdate) {
        onUpdate({ config: newConfig, seed, previewUrl: url, gender: newGender, svgContent });
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate avatar preview');
    } finally {
      setIsLoading(false);
    }
  }, [isEditing, onUpdate]);

  useEffect(() => {
    const id = setTimeout(() => generatePreview(config, gender), 300);
    return () => clearTimeout(id);
  }, [config, gender]);

  const skinColorOptions = AVATAR_CONFIG?.OPTIONS?.skinColor?.map(c => ({ id: c, color: `#${c}` })) || [];
  const clothingColorOptions = [
    { id: 'none', color: null },
    ...(AVATAR_CONFIG?.OPTIONS?.clothingColor?.map(c => ({ id: c, color: `#${c}` })) || [])
  ];
  const faceOptions = [
    { id: 'none', label: 'None', url: avatarService?.buildDiceBearURL({}, AVATAR_CONFIG?.SEEDS?.[gender], gender) },
    ...(AVATAR_CONFIG?.OPTIONS?.face?.map(f => ({
      id: f,
      label: FACE_DISPLAY_NAMES?.[f] || f?.replace(/([A-Z])/g, ' $1')?.trim(),
      url: avatarService?.buildStaticThumbnailURL('face', f, AVATAR_CONFIG?.SEEDS?.[gender])
    })) || [])
  ];
  const facialHairOptions = [
    { id: 'none', label: 'None', url: avatarService?.buildDiceBearURL({}, AVATAR_CONFIG?.SEEDS?.male, 'male') },
    ...(AVATAR_CONFIG?.OPTIONS?.facialHair?.map(s => ({
      id: s,
      label: s?.charAt(0)?.toUpperCase() + s?.slice(1),
      url: avatarService?.buildStaticThumbnailURL('facialHair', s, AVATAR_CONFIG?.SEEDS?.male)
    })) || [])
  ];
  const headOptions = [
    { id: 'none', label: 'None', url: avatarService?.buildDiceBearURL({}, AVATAR_CONFIG?.SEEDS?.[gender], gender) },
    ...(AVATAR_CONFIG?.OPTIONS?.head?.map(s => ({
      id: s,
      label: s?.replace(/([A-Z])/g, ' $1')?.trim(),
      url: avatarService?.buildStaticThumbnailURL('head', s, AVATAR_CONFIG?.SEEDS?.[gender])
    })) || [])
  ];
  const accessoriesOptions = [
    { id: 'none', label: 'None', url: avatarService?.buildDiceBearURL({}, AVATAR_CONFIG?.SEEDS?.[gender], gender) },
    ...(AVATAR_CONFIG?.OPTIONS?.accessories?.map(a => ({
      id: a,
      label: a?.charAt(0)?.toUpperCase() + a?.slice(1),
      url: avatarService?.buildStaticThumbnailURL('accessories', a, AVATAR_CONFIG?.SEEDS?.[gender])
    })) || [])
  ];

  const tabs = [
    { id: 'skinColor', label: 'Skin', icon: Palette },
    { id: 'face', label: 'Face', icon: Smile },
    { id: 'hair', label: 'Hair', icon: User },
    ...(gender === 'male' ? [{ id: 'facialHair', label: 'Beard', icon: User }] : []),
    { id: 'clothingColor', label: 'Outfit', icon: Palette },
    { id: 'accessories', label: 'Extras', icon: Glasses },
  ];

  const handleOptionSelect = (category, value) => {
    if (!isEditing) return;
    setConfig(prev => {
      const next = { ...prev };
      if (value === 'none') delete next?.[category];
      else next[category] = value;
      return next;
    });
  };

  const handleGenderChange = (g) => {
    if (!isEditing) return;
    setGender(g);
    setConfig({});
    if (g === 'female' && activeTab === 'facialHair') setActiveTab('skinColor');
  };

  const handleRandomize = () => {
    if (!isEditing) return;
    const pick = (arr) => arr?.[Math.floor(Math.random() * arr?.length)]?.id;
    const rc = {
      skinColor: pick(skinColorOptions),
      clothingColor: pick(clothingColorOptions?.filter(o => o?.id !== 'none')),
      face: pick(faceOptions?.filter(o => o?.id !== 'none')),
      head: pick(headOptions?.filter(o => o?.id !== 'none')),
      accessories: Math.random() > 0.5 ? pick(accessoriesOptions?.filter(o => o?.id !== 'none')) : undefined,
    };
    if (gender === 'male' && Math.random() > 0.4) {
      rc.facialHair = pick(facialHairOptions?.filter(o => o?.id !== 'none'));
    }
    Object.keys(rc)?.forEach(k => rc?.[k] === undefined && delete rc?.[k]);
    setConfig(rc);
  };

  const tabIdx = tabs?.findIndex(t => t?.id === activeTab);
  const goNext = () => { if (tabIdx < tabs?.length - 1) setActiveTab(tabs?.[tabIdx + 1]?.id); };
  const goPrev = () => { if (tabIdx > 0) setActiveTab(tabs?.[tabIdx - 1]?.id); };

  const renderColorGrid = (options, category) => (
    <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
      {options?.map(opt => {
        const isSelected = config?.[category] === opt?.id || (!config?.[category] && opt?.id === 'none');
        return (
          <button
            key={opt?.id}
            onClick={() => handleOptionSelect(category, opt?.id)}
            disabled={!isEditing}
            title={opt?.id === 'none' ? 'None' : `#${opt?.id}`}
            className={`relative aspect-square rounded-lg border-2 transition-all ${
              isSelected ? 'border-primary ring-2 ring-primary/30 scale-105' : 'border-border hover:border-primary/50'
            } ${!isEditing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {opt?.id === 'none' ? (
              <div className="w-full h-full rounded-md flex items-center justify-center">
                <span className="text-[10px] text-muted-foreground font-medium">None</span>
              </div>
            ) : (
              <div className="w-full h-full rounded-md" style={{ backgroundColor: opt?.color }} />
            )}
            {isSelected && opt?.id !== 'none' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Check className="w-3 h-3 text-white drop-shadow" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );

  const renderImageGrid = (options, category) => (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {options?.map(opt => {
        const isSelected = config?.[category] === opt?.id || (!config?.[category] && opt?.id === 'none');
        return (
          <button
            key={opt?.id}
            onClick={() => handleOptionSelect(category, opt?.id)}
            disabled={!isEditing}
            title={opt?.label}
            className={`relative rounded-lg border-2 overflow-hidden transition-all ${
              isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/50'
            } ${!isEditing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <img src={opt?.url} alt={opt?.label} className="w-full aspect-square object-contain bg-muted/30" />
            <div className={`absolute bottom-0 left-0 right-0 py-0.5 text-center text-[9px] font-medium truncate px-1 ${
              isSelected ? 'bg-primary text-primary-foreground' : 'bg-background/80 text-muted-foreground'
            }`}>
              {opt?.label}
            </div>
            {isSelected && (
              <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                <Check className="w-2.5 h-2.5 text-white" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'skinColor': return renderColorGrid(skinColorOptions, 'skinColor');
      case 'clothingColor': return renderColorGrid(clothingColorOptions, 'clothingColor');
      case 'face': return renderImageGrid(faceOptions, 'face');
      case 'facialHair': return renderImageGrid(facialHairOptions, 'facialHair');
      case 'hair': return renderImageGrid(headOptions, 'head');
      case 'accessories': return renderImageGrid(accessoriesOptions, 'accessories');
      default: return null;
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-heading font-bold text-foreground">Avatar Builder</h2>
            <p className="text-xs text-muted-foreground">Customize your look</p>
          </div>
        </div>
        {isEditing && (
          <button
            onClick={handleRandomize}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
          >
            <Shuffle className="w-3.5 h-3.5" />
            Randomize
          </button>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Avatar Preview - compact */}
        <div className="flex flex-col items-center gap-3 sm:w-32 flex-shrink-0">
          {/* Gender toggle */}
          <div className="flex w-full rounded-lg border border-border overflow-hidden">
            {[{ id: 'male', label: 'M' }, { id: 'female', label: 'F' }]?.map(g => (
              <button
                key={g?.id}
                onClick={() => handleGenderChange(g?.id)}
                disabled={!isEditing}
                className={`flex-1 py-1.5 text-xs font-semibold transition-all ${
                  gender === g?.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                } ${!isEditing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {g?.label === 'M' ? '♂ Male' : '♀ Female'}
              </button>
            ))}
          </div>

          {/* Avatar circle */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/10 to-primary/20 border-2 border-primary/30 overflow-hidden flex items-center justify-center shadow-md">
              {isLoading ? (
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              ) : previewUrl ? (
                <img src={previewUrl} alt="Avatar preview" className="w-full h-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-muted-foreground" />
              )}
            </div>
            {isLoading && (
              <div className="absolute inset-0 rounded-full bg-background/50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
            )}
          </div>

          {!isEditing && (
            <p className="text-[11px] text-center text-muted-foreground leading-tight">
              Click "Edit Profile" to customize
            </p>
          )}
          {isEditing && (
            <p className="text-[10px] text-center text-muted-foreground leading-tight">
              Updates in real-time
            </p>
          )}
        </div>

        {/* Customization Panel */}
        <div className="flex-1 min-w-0">
          {/* Tab Pills */}
          <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1 scrollbar-hide">
            {tabs?.map(tab => {
              const TabIcon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  disabled={!isEditing}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : isEditing
                      ? 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                      : 'bg-muted text-muted-foreground opacity-50 cursor-not-allowed'
                  }`}
                >
                  <TabIcon className="w-3 h-3" />
                  {tab?.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="bg-muted/30 rounded-xl p-3 min-h-[160px] max-h-[280px] overflow-y-auto">
            {renderTabContent()}
          </div>

          {/* Prev / Next navigation */}
          {isEditing && (
            <div className="flex items-center justify-between mt-2">
              <button
                onClick={goPrev}
                disabled={tabIdx === 0}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                {tabIdx > 0 ? tabs?.[tabIdx - 1]?.label : ''}
              </button>
              <span className="text-[10px] text-muted-foreground">{tabIdx + 1} / {tabs?.length}</span>
              <button
                onClick={goNext}
                disabled={tabIdx === tabs?.length - 1}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
              >
                {tabIdx < tabs?.length - 1 ? tabs?.[tabIdx + 1]?.label : ''}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
      {error && (
        <p className="mt-3 text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
      )}
    </div>
  );
};

export default AvatarBuilder;